// TMOS Objective Performance Page
// Date: January 13, 2026
// Purpose: Display detailed objective performance with target vs actual

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { HealthIndicator } from '@/components/business-performance/HealthIndicator';
import { ProgressBar } from '@/components/business-performance/ProgressBar';
import { businessPerformanceService } from '@/services/businessPerformanceService';
import { ObjectivePerformance } from '@/types/businessPerformance';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  FlagIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';

export const ObjectivePerformancePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan_id');
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [performance, setPerformance] = useState<ObjectivePerformance | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadObjectivePerformance();
    }
  }, [id, planId]);

  const loadObjectivePerformance = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await businessPerformanceService.getObjectivePerformance(id, planId || undefined);
      setPerformance(data);
    } catch (error: any) {
      console.error('Error loading objective performance:', error);
      toast.error(error.message || t('businessPerformance.errorLoadingObjectivePerformance'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-primary-bg)] p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-[var(--color-secondary-text)]">Loading objective performance...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!performance) {
    return (
      <div className="min-h-screen bg-[var(--color-primary-bg)] p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-[var(--color-secondary-text)] mb-4">
              {t('businessPerformance.objectiveNotFound') || 'Objective not found'}
            </p>
            <Button onClick={() => navigate('/performance')} variant="primary">
              {t('common.backToDashboard') || 'Back to Dashboard'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { objective, progress, trend_data } = performance;
  const variance = progress.variance;
  const variancePercent = progress.variance_percent;

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate(planId ? `/performance/plans/${planId}` : '/performance')}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            {t('common.back') || 'Back'}
          </Button>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FlagIcon className="w-6 h-6 text-[var(--color-info)]" />
                <h1 className="text-3xl font-bold text-[var(--color-surface-text)]">
                  {objective.name}
                </h1>
              </div>
              {objective.description && (
                <p className="text-[var(--color-secondary-text)] mb-4">
                  {objective.description}
                </p>
              )}
              <div className="flex items-center gap-4">
                {objective.priority && (
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                    {t('businessPerformance.priority') || 'Priority'}: {objective.priority}
                  </span>
                )}
                {objective.status && (
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                    {objective.status}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="mb-2">
                <HealthIndicator
                  status={progress.status === 'ON_TRACK' ? 'GREEN' : progress.status === 'AT_RISK' ? 'YELLOW' : 'RED'}
                  score={progress.progress_percent}
                  showScore={true}
                  size="lg"
                />
              </div>
              <p className="text-sm text-[var(--color-secondary-text)]">
                {t('businessPerformance.progress') || 'Progress'}
              </p>
            </div>
          </div>
        </div>

        {/* Target vs Actual */}
        <Card variant="outline" className="mb-6">
          <CardContent className="p-4">
            <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
              {t('businessPerformance.targetVsActual') || 'Target vs Actual'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-sm text-[var(--color-secondary-text)] mb-1">
                  {t('businessPerformance.target') || 'Target'}
                </p>
                <p className="text-2xl font-bold text-[var(--color-surface-text)]">
                  {objective.target_value} {objective.target_unit || ''}
                </p>
              </div>
              <div>
                <p className="text-sm text-[var(--color-secondary-text)] mb-1">
                  {t('businessPerformance.actual') || 'Actual'}
                </p>
                <p className="text-2xl font-bold text-[var(--color-surface-text)]">
                  {progress.current_actual !== null ? `${progress.current_actual.toFixed(2)} ${objective.target_unit || ''}` : t('businessPerformance.noData') || 'No Data'}
                </p>
              </div>
              <div>
                <p className="text-sm text-[var(--color-secondary-text)] mb-1">
                  {t('businessPerformance.variance') || 'Variance'}
                </p>
                <div className="flex items-center gap-2">
                  <p className={`text-2xl font-bold ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {variance >= 0 ? '+' : ''}{variance.toFixed(2)} {objective.target_unit || ''}
                  </p>
                  {variance >= 0 ? (
                    <ArrowTrendingUpIcon className="w-5 h-5 text-green-600" />
                  ) : (
                    <ArrowTrendingDownIcon className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <p className={`text-sm ${variancePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {variancePercent >= 0 ? '+' : ''}{variancePercent.toFixed(1)}%
                </p>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-[var(--color-secondary-text)]">
                  {t('businessPerformance.progress') || 'Progress'}
                </span>
                <span className="text-sm font-semibold text-[var(--color-surface-text)]">
                  {progress.progress_percent.toFixed(1)}%
                </span>
              </div>
              <ProgressBar
                progress={progress.progress_percent}
                color={progress.status === 'ON_TRACK' ? 'success' : progress.status === 'AT_RISK' ? 'warning' : 'danger'}
                height="md"
              />
            </div>
          </CardContent>
        </Card>

        {/* Trend Chart Placeholder */}
        {trend_data && trend_data.length > 0 && (
          <Card variant="outline" className="mb-6">
            <CardContent className="p-4">
              <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
                {t('businessPerformance.historicalTrend') || 'Historical Trend'}
              </h2>
              <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
                <div className="text-center">
                  <ChartBarIcon className="w-12 h-12 text-[var(--color-secondary-text)] mx-auto mb-2 opacity-50" />
                  <p className="text-sm text-[var(--color-secondary-text)]">
                    {t('businessPerformance.chartPlaceholder') || 'Chart visualization will be implemented here'}
                  </p>
                  <p className="text-xs text-[var(--color-secondary-text)] mt-1">
                    {trend_data.length} {t('businessPerformance.dataPoints') || 'data points'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPI Information */}
        {objective.kpi_id && (
          <Card variant="outline">
            <CardContent className="p-4">
              <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
                {t('businessPerformance.kpiInformation') || 'KPI Information'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[var(--color-secondary-text)] mb-1">
                    {t('businessPerformance.kpiId') || 'KPI ID'}
                  </p>
                  <p className="text-[var(--color-surface-text)]">
                    {objective.kpi_id}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[var(--color-secondary-text)] mb-1">
                    {t('businessPerformance.targetUnit') || 'Target Unit'}
                  </p>
                  <p className="text-[var(--color-surface-text)]">
                    {objective.target_unit || t('common.none') || 'None'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

